// Inbound Requests combo: one stacked bar per time slot (status segments + ping-shield event
// types) over a cyan area-fill Requests line drawn behind the stack.
// Mirrors calls_receivables_and_payables_combo.js.
//
// Points arrive as {x: bucket epoch ms, y: count} on a dynamic bucket grid (data.timeScale),
// so the x axis is a Chart.js time scale rendered in the account time zone via the luxon adapter.
import { Chart } from 'chart.js'

import { chartPalette, barDepthFill, barCapBorderRadius, verticalGradient } from './palette'
import { dragZoomPlugin } from './drag_zoom_plugin'
import { resolveColor, fillGaps, markNoData, noDataPlugin, timeXScaleOptions } from './time_axis'

export function buildInboundRequestsByStatus(canvas, optionsFromParam, { plugins = [] } = {}) {
    const {
        datasets: sourceDatasets = [],
        timeScale = {},
        noDataMessage,
    } = optionsFromParam.data || {}
    const palette = chartPalette()

    const requestsDataset = sourceDatasets[0] || {}
    const barStatusDatasets = sourceDatasets.slice(1)

    const noData = markNoData(canvas, sourceDatasets)

    const intervalMs = (timeScale.intervalSeconds || 0) * 1000
    const gapFilledPoints = (points) => {
        return fillGaps(points, intervalMs, timeScale.windowStart, timeScale.windowEnd)
    }

    // Every bar shares one stack, so each time slot renders as a single composition column.
    // The explicit stack id also keeps the Requests line (stack undefined) out of
    // barCapBorderRadius's top-of-stack lookup.
    const barBase = {
        type: 'bar',
        yAxisID: 'y',
        barPercentage: 0.92,
        categoryPercentage: 0.78,
        stack: 'inbound',
        order: 2,
    }

    // The empty state renders axes only: the bar and line controllers have nothing to
    // draw, and Chart.js 4's time-scale bar layout crashes outright on zero points.
    const datasets = noData
        ? []
        : barStatusDatasets.map((dataset) => {
              const rawColor = dataset.borderColor || dataset.backgroundColor
              const color = resolveColor(rawColor)
              return {
                  ...barBase,
                  label: dataset.label,
                  data: gapFilledPoints(dataset.data),
                  backgroundColor: barDepthFill(color),
                  borderColor: color,
              }
          })

    if (!noData) {
        datasets.push({
            type: 'line',
            label: requestsDataset.label,
            yAxisID: 'y',
            data: gapFilledPoints(requestsDataset.data),
            borderColor: palette.callsLine,
            backgroundColor: verticalGradient([
                [0, palette.areaFillTop],
                [1, palette.areaFillBottom],
            ]),
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointBackgroundColor: palette.callsLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5,
            borderJoinStyle: 'round',
            fill: true,
            // Higher order than the bars (2) draws the line first, i.e. behind the stack.
            order: 1,
        })
    }

    return new Chart(canvas, {
        plugins: [
            ...plugins,
            dragZoomPlugin(intervalMs, palette.callsLine),
            ...(noData
                ? [
                      noDataPlugin(
                          noDataMessage || 'No data for the selected time frame',
                          palette.ink,
                      ),
                  ]
                : []),
        ],
        data: { datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 0,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (item) => {
                            const value = Number(item.parsed.y).toLocaleString()
                            return `${item.dataset.label}: ${value}`
                        },
                    },
                },
            },
            scales: {
                x: timeXScaleOptions(timeScale, { stacked: true }),
                y: {
                    stacked: true,
                    beginAtZero: true,
                    grid: { drawTicks: false },
                    border: { display: false },
                    // Counts are integers; precision matters for the empty chart, whose
                    // default 0–1 scale would otherwise tick at 0.2 steps.
                    ticks: { padding: 8, precision: 0 },
                },
            },
            elements: {
                bar: {
                    borderRadius: barCapBorderRadius(false, true),
                    borderSkipped: false,
                },
            },
            animation: { duration: 320, easing: 'easeOutCubic' },
        },
    })
}
